import React from 'react';
import { useForm } from 'react-hook-form';
import { CartPanda as Icon, SimpleCheck } from '@profitfy/pakkun-icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { useHistory, useParams } from 'react-router-dom';

import { EHeadingWeight } from '@domain/enums/components/EHeading';
import { ETextSize } from '@domain/enums/components/EText';
import { IParams } from '@domain/interfaces/IParams';

import { useToast } from '@helpers/hooks/useToast';
import { useDropshipping } from '@helpers/hooks/pages/onboarding/useDropshipping';

import { getTranslatedIntegrationsError } from '@helpers/utils/errors/integrations/integrations';

import { cartPandaSchema } from '@helpers/validators/dashboard/integrationCenter/cartPanda';

import cartPandaPayService from '@services/pages/dashboard/integrationCenter/gateway/cartPandaPay';

import NextStep from '@components/Onboarding/NextStep';
import Text from '@components/common/core/DataDisplay/Text';

import * as S from './styles';

const CartPandaPay: React.FC = () => {
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm({ mode: 'onBlur', resolver: yupResolver(cartPandaSchema) });
  const { handleNextGatewayToIntegrate, selectedMarketings } = useDropshipping();
  const { storeAliasId } = useParams<IParams>();
  const history = useHistory();
  const { toast } = useToast();

  const [isLoadingSubmittedForm, setIsLoadingSubmittedForm] = React.useState<boolean>(false);
  const [domainValue, setDomainValue] = React.useState<string>('');
  const [accessTokenValue, setAccessTokenValue] = React.useState<string>('');

  const domainRegister = register('domain');
  const accessTokenRegister = register('access_token');

  const onDomainValueChange = React.useCallback(
    event => {
      domainRegister.onChange(event);

      setDomainValue(event.target.value);
    },
    [domainRegister],
  );

  const onAccessTokenValueChange = React.useCallback(
    event => {
      accessTokenRegister.onChange(event);

      setAccessTokenValue(event.target.value);
    },
    [accessTokenRegister],
  );

  const sanitizeUrl = React.useCallback(url => {
    const replacedUrl = url.replace('https://', '').replace('wwww.', '');

    const parsedUrl = replacedUrl.split('.mycartpanda.com')[0];

    return parsedUrl;
  }, []);

  const onSubmit = React.useCallback(
    async data => {
      setIsLoadingSubmittedForm(true);

      const sanitizedUrl = sanitizeUrl(data.domain);

      try {
        await cartPandaPayService.createCredential({
          storeAliasId,
          data: { access_token: data.access_token, domain: sanitizedUrl },
        });

        toast.success('Credencial salva com sucesso!');

        setIsLoadingSubmittedForm(false);

        const hasNextGateway = handleNextGatewayToIntegrate();

        if (!hasNextGateway && selectedMarketings.length) {
          history.push(`/onboarding/${storeAliasId}/dropshipping/integrations/marketing`);
        }

        if (!hasNextGateway && !selectedMarketings.length) {
          history.push(
            `/onboarding/${storeAliasId}/dropshipping/integrations/integration-finished`,
          );
        }
      } catch (error: any) {
        setIsLoadingSubmittedForm(false);
        toast.error(getTranslatedIntegrationsError(error));
      }
    },
    [history, handleNextGatewayToIntegrate, storeAliasId, selectedMarketings, toast, sanitizeUrl],
  );

  const hasAllInputsValue = domainValue && accessTokenValue;

  return (
    <S.Wrapper>
      <S.FormWrapper>
        <S.Form onSubmit={handleSubmit(onSubmit)}>
          <S.Header>
            <S.Title>Integre com Gateway de pagamento</S.Title>
          </S.Header>

          <S.IntegrationTitle>
            <Icon size={36} />

            <S.IntegrationTitleText fontWeight={EHeadingWeight.REGULAR}>
              Integração com CartPanda Pay
            </S.IntegrationTitleText>
          </S.IntegrationTitle>

          <S.Body>
            <S.BenefitsWrapper>
              <S.Benefit>
                <SimpleCheck size={12} />
                <S.DescriptionText size={ETextSize.B5}>Exatidão dos dados</S.DescriptionText>
              </S.Benefit>
              <S.Benefit>
                <SimpleCheck size={12} />
                <S.DescriptionText size={ETextSize.B5}>Confiabilidade dos dados</S.DescriptionText>
              </S.Benefit>
            </S.BenefitsWrapper>

            <S.InputGroup>
              <S.Label htmlFor="cart-panda-store-url">URL da Loja</S.Label>
              <S.InputWrapper isError={errors.domain} value={domainValue}>
                <S.TextField
                  {...domainRegister}
                  type="text"
                  id="cart-panda-store-url"
                  onChange={onDomainValueChange}
                />
                <S.AppendContent>.mycartpanda.com</S.AppendContent>
              </S.InputWrapper>
              {errors.domain && <Text isErrorFeedback>{errors.domain.message}</Text>}
            </S.InputGroup>

            <S.InputGroup>
              <S.Label htmlFor="access-token">Access Token</S.Label>

              <S.TextField
                {...accessTokenRegister}
                type="text"
                id="access-token"
                onChange={onAccessTokenValueChange}
                value={accessTokenValue}
                isError={errors.access_token}
              />
              {errors.access_token && <Text isErrorFeedback>{errors.access_token.message}</Text>}
            </S.InputGroup>
          </S.Body>

          <NextStep
            type="submit"
            isLoading={isLoadingSubmittedForm}
            disabled={isLoadingSubmittedForm || !hasAllInputsValue}
          >
            Próximo passo
          </NextStep>
        </S.Form>
      </S.FormWrapper>

      <S.TutorialWrapper>
        <S.TutorialImage
          src="https://s3.us-west-2.amazonaws.com/app.profitfy.me-media/onboarding/images/content/onboarding/onboarding-integration-tutorial.svg"
          alt="Profitfy.me Integration Tutorial"
        />
        <S.TutorialTitle>Tutorial de integração</S.TutorialTitle>
        <S.TutorialContent>
          Para realizar a integração você precisará do <strong>Domínio</strong> e do{' '}
          <strong>Access Token</strong> que são encontrados neste link:
        </S.TutorialContent>
        <S.TutorialLink
          href="https://ajuda.profitfy.me/pt-br/articles/6060422-como-integrar-com-a-cartpanda-pay"
          target="_blank"
        >
          Encontre suas credenciais clicando aqui!
        </S.TutorialLink>
      </S.TutorialWrapper>
    </S.Wrapper>
  );
};

export default CartPandaPay;
